import { Accordion, AccordionDetails, Grid, Typography } from "@mui/material";
import AccordionSummarySlim from "components/Common/AccordionSummarySlim";
import CheckBoxForm from "components/Common/CheckBoxForm";
import SelectForm from "components/Common/SelectForm";
import TextFieldForm from "components/Common/TextFieldForm";
import Constants from "helpers/constants";
import useMeasurementTypes from "helpers/context/SelectionValues/useMeaurementTypes";
import { useTranslations } from "@fenetech/translations";
import React, { useMemo } from 'react';
import { useFormContext } from "react-hook-form";
import CustomerStatusChips from "./CustomerStatusChips";
import { IAssignableUser } from "models/IWebCustomer";
import AssignableUsers from "./AssignableUsers";
import useUserInfo from "helpers/context/User/useUserInfo";
import { RoleEnum } from "helpers/enums";
import LabeledText from "components/Common/LabeledText";

interface IProps {
    assignableUsers: IAssignableUser[],
    mfgSiteName: string,
    expanded: boolean,
    onExpandedChange: (event: React.SyntheticEvent, expanded: boolean) => void,
    readOnly: boolean
}

const CustomerGeneral: React.FC<IProps> = ({assignableUsers, mfgSiteName, expanded, onExpandedChange, readOnly}: IProps) => {

    const tm = useTranslations();
    const user = useUserInfo();
    const measurementTypes = useMeasurementTypes();
    const { watch, formState: { errors } } = useFormContext();

    const isInactive = watch("customer.inactive");
    const isDefault = watch("customer.default");
    const showStatusChips = readOnly && (isInactive || isDefault);

    const mfgSiteNameVisible = useMemo(() => {
        return !user.HasRole(RoleEnum.Contractor);
    }, [user]);

    return <>
        <Accordion expanded={expanded} onChange={onExpandedChange} disableGutters >
            <AccordionSummarySlim>
                <Typography>
                    {tm.Get("General")}
                </Typography>
            </AccordionSummarySlim>
            <AccordionDetails>
                <Grid container spacing={2} padding={0}>
                    {mfgSiteNameVisible &&
                        <Grid item xs={12} sm={showStatusChips ? 6 : 12}>
                            <LabeledText
                                label={tm.Get("Mfg Site")}
                                text={mfgSiteName}
                            />
                        </Grid>
                    }
                    {(showStatusChips && mfgSiteNameVisible) &&
                        <Grid item container xs={12} sm={6} sx={{justifyContent: { sm: "flex-end" }}}>
                            <CustomerStatusChips isInactive={isInactive} isDefault={isDefault} />
                        </Grid>
                    }
                    <Grid item xs={12} sm={6}>
                        <TextFieldForm
                            fieldName="customer.name"
                            readOnly={readOnly}
                            requiredRule={{ value: true, message: tm.GetWithParams("{0} Required", tm.Get("Name")) }}
                            validateValue={{
                                isNotEmpty: (value: string) => new RegExp("^(?!\\s*$).+").test(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get("Name"))
                            }}
                            error={errors.customer?.name?.message ? true : false}
                            helperText={errors.customer?.name?.message}
                            variant="standard"
                            id="customer.name"
                            fullWidth
                            inputProps={{ maxLength: Constants.MaxLength.CustomerName }}
                            label={tm.Get("Name")}
                            placeholder={tm.Get("Name")} />
                    </Grid>
                    {/* These chips need to be in the top right, however the site name can show/hide which changes the layout order. 
                    To solve this we can declare the chips twice, and always hide one of them */}
                    {(showStatusChips && !mfgSiteNameVisible) &&
                        <Grid item container xs={12} sm={6} sx={{justifyContent: { sm: "flex-end" }}}>
                            <CustomerStatusChips isInactive={isInactive} isDefault={isDefault} />
                        </Grid>
                    }
                    <Grid item container xs={12}>
                        {!readOnly &&
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <CheckBoxForm
                                    fieldName="customer.inactive"
                                    label={tm.Get("Inactive")}
                                    id="customer.inactive"
                                    readOnly={readOnly}
                                />
                            </Grid>
                        }
                        {!readOnly &&
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <CheckBoxForm
                                    fieldName="customer.default"
                                    label={tm.Get("Default")}
                                    id="customer.default"
                                    readOnly={readOnly}
                                />
                            </Grid>
                        }
                    </Grid>
                    <AssignableUsers
                        assignableUsers={assignableUsers}
                        readOnly={readOnly}
                    />
                    
                    <Grid item xs={12}>
                        <TextFieldForm
                            fieldName="customer.notes"
                            readOnly={readOnly}
                            variant="outlined"
                            id="customer.notes"
                            fullWidth
                            inputProps={{ maxLength: Constants.MaxLength.CustomerNotes }}
                            label={tm.Get("Notes")}
                            placeholder={tm.Get("Notes")}
                            multiline={true}
                            rows={2} />
                    </Grid>
                    <Grid item xs={12}>
                        <SelectForm
                            fieldName="customer.engineeringUnitSetID"
                            dataSource={measurementTypes}
                            keyField="setID"
                            valueField="setID"
                            displayField="description"
                            translateDisplayField={true}
                            label={tm.Get("Measurement Type")}
                            readOnly={readOnly}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    </>;
}

export default CustomerGeneral;